import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Title,
  Text,
  Container,
  SimpleGrid,
  Paper,
  ThemeIcon,
  Textarea,
  Group,
  rem,
  Stack,
  Badge,
  ActionIcon,
  Modal,
  TextInput,
  Button,
  Image,
  Center,
  Loader,
  Box,
  Divider,
} from '@mantine/core';
import {
  IconHeadset,
  IconShoppingCart,
  IconChartBar,
  IconUsers,
  IconCar,
  IconFileText,
  IconSettings,
  IconPhone,
  IconDeviceDesktop,
  IconSearch,
  IconCalendarEvent,
  IconRocket,
  IconPaperclip,
  IconToolsKitchen2,
  IconLayoutDashboard,
  IconMessageCircle2,
  IconSparkles,
  IconArrowUpRight,
  IconBuildingSkyscraper,
} from '@tabler/icons-react';
import dayjs from 'dayjs';
import 'dayjs/locale/tr';
import api from '../api/axiosConfig';
import PhoneDirectoryModal from '../components/PhoneDirectoryModal';

dayjs.locale('tr');

const IconMap = {
  IconHeadset,
  IconShoppingCart,
  IconChartBar,
  IconUsers,
  IconCar,
  IconFileText,
  IconSettings,
  IconPhone,
  IconDeviceDesktop,
  IconSearch,
  IconCalendarEvent,
  IconRocket,
  IconPaperclip,
  IconToolsKitchen2,
};

const heroGradient =
  'linear-gradient(135deg, rgba(15,23,42,0.98) 0%, rgba(30,41,59,0.96) 35%, rgba(37,99,235,0.88) 100%)';

export default function MainPortal() {
  const navigate = useNavigate();

  const [stats, setStats] = useState({
    active_guests: 0,
    vehicles_on_duty: 0,
    todays_checks: 0,
  });

  const [quote, setQuote] = useState('');
  const [isPhoneModalOpen, setIsPhoneModalOpen] = useState(false);
  const [isComingSoonOpen, setIsComingSoonOpen] = useState(false);
  const [isMenuModalOpen, setIsMenuModalOpen] = useState(false);

  const [menuData, setMenuData] = useState({
    menu_url: '',
    updated_at: '',
    loading: true,
  });

  const [activeModuleName, setActiveModuleName] = useState('');
  const [activeModuleDescription, setActiveModuleDescription] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [userName, setUserName] = useState('Kullanıcı');
  const [userRole, setUserRole] = useState(
    localStorage.getItem('user_role') || 'personnel'
  );

  const [apps, setApps] = useState([]);
  const [appsLoading, setAppsLoading] = useState(true);

  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [feedbackSending, setFeedbackSending] = useState(false);
  const [feedbackSuccess, setFeedbackSuccess] = useState(false);

  useEffect(() => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        const payloadBase64 = token.split('.')[1];
        const decodedPayload = JSON.parse(atob(payloadBase64));

        if (decodedPayload.name) setUserName(decodedPayload.name);
        else if (decodedPayload.sub) setUserName(decodedPayload.sub);

        if (decodedPayload.role) setUserRole(decodedPayload.role);
      }
    } catch (e) {
      console.error('Token hatası:', e);
    }

    const initDashboard = async () => {
      try {
        const [menuRes, quotesRes, appsRes] = await Promise.all([
          api
            .get('/dashboard/cafeteria-menu')
            .catch(() => ({ data: { menu_url: null, updated_at: null } })),
          api.get('/dashboard/quotes').catch(() => ({ data: [] })),
          api.get('/dashboard/apps').catch(() => ({ data: [] })),
        ]);

        if (quotesRes?.data && quotesRes.data.length > 0) {
          const randomIndex = Math.floor(Math.random() * quotesRes.data.length);
          setQuote(quotesRes.data[randomIndex].content);
        } else {
          setQuote('Başarı; düzen, istikrar ve doğru araçlarla daha sürdürülebilir hale gelir.');
        }

        if (menuRes?.data) {
          setMenuData({
            menu_url: menuRes.data.menu_url,
            updated_at: menuRes.data.updated_at,
            loading: false,
          });
        }

        if (appsRes?.data) {
          setApps(appsRes.data);
        }
      } catch (error) {
        console.warn('Veriler çekilemedi.', error);
        setMenuData((prev) => ({ ...prev, loading: false }));
      } finally {
        setAppsLoading(false);
      }
    };

    initDashboard();
  }, []);

  const visibleApps = useMemo(() => {
    const filteredByRole = apps.filter((app) => {
      if (userRole === 'admin' || userRole === 'Yönetici') return true;
      if (!Array.isArray(app.allowed_roles)) return false;
      if (app.allowed_roles.includes('*')) return true;
      return app.allowed_roles.includes(userRole);
    });

    if (!searchTerm.trim()) return filteredByRole;

    const q = searchTerm.toLowerCase('tr-TR');
    return filteredByRole.filter((app) => {
      return (
        app.title?.toLowerCase('tr-TR').includes(q) ||
        app.description?.toLowerCase('tr-TR').includes(q)
      );
    });
  }, [userRole, apps, searchTerm]);

  const handleCardClick = (path, title, comingSoon, description = false) => {
    if (path === 'modal-feedback') {
      setIsFeedbackModalOpen(true);
      return;
    }

    if (path === 'modal-phone') {
      setIsPhoneModalOpen(true);
      return;
    }

    if (path === 'modal-menu') {
      setIsMenuModalOpen(true);
      return;
    }

    if (comingSoon) {
      setActiveModuleName(title);
      setActiveModuleDescription(description);
      setIsComingSoonOpen(true);
      return;
    }

    navigate(path);
  };

  const getAppStat = (app) => {
    if (
      !app.stat_key ||
      stats[app.stat_key] === undefined ||
      stats[app.stat_key] === null ||
      stats[app.stat_key] === 0
    ) {
      return null;
    }

    return `${stats[app.stat_key]} ${app.stat_label || ''}`;
  };

  const roleLabel =
    userRole === 'admin' || userRole === 'Yönetici'
      ? 'Yönetici'
      : String(userRole || 'Personel');

  return (
    <Container fluid p={{ base: 'sm', md: 'lg' }}>
      <Stack gap="xl">
        {/* HERO */}
        <Paper
          radius="xl"
          p={{ base: 'lg', md: 'xl' }}
          style={{
            position: 'relative',
            overflow: 'hidden',
            background: heroGradient,
            border: '1px solid rgba(255,255,255,0.08)',
            boxShadow: '0 20px 50px rgba(15,23,42,0.18)',
          }}
        >
          <Box
            style={{
              position: 'absolute',
              top: -80,
              right: -80,
              width: 240,
              height: 240,
              borderRadius: '50%',
              background: 'rgba(255,255,255,0.08)',
              filter: 'blur(4px)',
            }}
          />
          <Box
            style={{
              position: 'absolute',
              bottom: -60,
              left: -60,
              width: 180,
              height: 180,
              borderRadius: '50%',
              background: 'rgba(96,165,250,0.18)',
              filter: 'blur(6px)',
            }}
          />

          <Stack gap="lg" style={{ position: 'relative', zIndex: 2 }}>
            <Group justify="space-between" align="flex-start" wrap="wrap">
              <Stack gap={6}>
                <Group gap="xs">
                  <ThemeIcon
                    size={38}
                    radius="xl"
                    variant="black"
                    color="rgba(255,255,255,0.14)"
                  >
                    <IconBuildingSkyscraper size={20} color="white" />
                  </ThemeIcon>

                  <Badge
                    radius="xl"
                    size="lg"
                    variant="white"
                    color="rgba(255,255,255,0.12)"
                    styles={{
                      root: {
                        color: 'white',
                        border: '1px solid rgba(255,255,255,0.14)',
                        background: 'rgba(255,255,255,0.08)',
                        backdropFilter: 'blur(8px)',
                      },
                    }}
                  >
                    {roleLabel}
                  </Badge>
                </Group>

                <Group gap="xs" c="rgba(255,255,255,0.82)">
                  <IconCalendarEvent size={16} />
                  <Text size="sm" fw={600}>
                    {dayjs().format('DD MMMM YYYY, dddd')}
                  </Text>
                </Group>

                <Title
                  order={1}
                  fw={900}
                  style={{
                    color: 'white',
                    letterSpacing: '-1px',
                    lineHeight: 1.1,
                  }}
                >
                  Hoş geldiniz, {userName}
                </Title>

                <Text
                  size="md"
                  maw={760}
                  style={{
                    color: 'rgba(255,255,255,0.78)',
                    lineHeight: 1.7,
                  }}
                >
                  Kurumsal portalınızdan modüllere hızlı erişebilir, operasyonel
                  verileri takip edebilir ve iş akışlarınızı tek ekrandan
                  yönetebilirsiniz.
                </Text>
              </Stack>

              <Paper
                radius="xl"
                p="md"
                style={{
                  minWidth: 280,
                  maxWidth: 420,
                  background: 'rgba(255,255,255,0.08)',
                  border: '1px solid rgba(255,255,255,0.12)',
                  backdropFilter: 'blur(12px)',
                }}
              >
                <Group align="flex-start" wrap="nowrap">
                  <ThemeIcon
                    size={40}
                    radius="xl"
                    variant="black"
                    color="rgba(255,255,255,0.14)"
                  >
                    <IconSparkles size={20} color="white" />
                  </ThemeIcon>

                  <Stack gap={4}>
                    <Text fw={700} c="white">
                      Günün notu
                    </Text>
                    <Text size="sm" style={{ color: 'rgba(255,255,255,0.82)' }}>
                      “{quote || 'Sistemsel veriler hazırlanıyor...'}”
                    </Text>
                  </Stack>
                </Group>
              </Paper>
            </Group>

            <Group justify="space-between" align="end" wrap="wrap" mt="xs">
              <TextInput
                placeholder="Modül ara..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.currentTarget.value)}
                leftSection={<IconSearch size={16} />}
                radius="xl"
                size="md"
                w={{ base: '100%', md: 360 }}
                styles={{
                  input: {
                    background: 'rgba(255,255,255,0.1)',
                    border: '1px solid rgba(255,255,255,0.16)',
                    color: 'white',
                    backdropFilter: 'blur(10px)',
                  },
                }}
              />
              <Group>
                <Badge radius="xl" size="lg" variant="light" color="white">
                {visibleApps.length} modül görüntüleniyor
                </Badge>
              </Group>

              <Group gap="sm">
                <Button
                  radius="xl"
                  variant="white"
                  leftSection={<IconPhone size={18} />}
                  onClick={() => setIsPhoneModalOpen(true)}
                >
                  Telefon Rehberi
                </Button>

                <Button
                  radius="xl"
                  variant="light"
                  color="gray"
                  leftSection={<IconMessageCircle2 size={18} />}
                  onClick={() => setIsFeedbackModalOpen(true)}
                  styles={{
                    root: {
                      background: 'rgba(255,255,255,0.12)',
                      color: 'white',
                      border: '1px solid rgba(255,255,255,0.12)',
                    },
                  }}
                >
                  Görüş Bildir
                </Button>
              </Group>
            </Group>
          </Stack>
        </Paper>

        {/* MODÜL ALANI */}
        <Stack gap="md">
          {appsLoading ? (
            <Center mt="xl" py={60}>
              <Stack align="center" gap="sm">
                <Loader size="lg" />
                <Text size="sm" c="dimmed">
                  Dashboard yükleniyor...
                </Text>
              </Stack>
            </Center>
          ) : visibleApps.length === 0 ? (
            <Paper
              radius="xl"
              p="xl"
              withBorder
              style={{
                background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
              }}
            >
              <Stack align="center" gap="xs" py="lg">
                <ThemeIcon size={64} radius="xl" variant="light" color="gray">
                  <IconSearch size={28} />
                </ThemeIcon>
                <Title order={4}>Sonuç bulunamadı</Title>
                <Text size="sm" c="dimmed" ta="center">
                  Arama kriterinize uygun modül bulunamadı. Farklı bir anahtar
                  kelime deneyebilirsiniz.
                </Text>
              </Stack>
            </Paper>
          ) : (
            <SimpleGrid cols={{ base: 1, sm: 2, lg: 3, xl: 4 }} spacing="lg">
              {visibleApps.map((app, index) => {
                const IconComponent = IconMap[app.icon] || IconLayoutDashboard;
                const statText = getAppStat(app);

                return (
                  <Paper
                    key={app.id}
                    withBorder
                    radius="xl"
                    p="lg"
                    onClick={() =>
                      handleCardClick(
                        app.path,
                        app.title,
                        app.coming_soon,
                        app.description
                      )
                    }
                    style={{
                      cursor: 'pointer',
                      position: 'relative',
                      overflow: 'hidden',
                      minHeight: 240,
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                      background:
                        'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
                      border: '1px solid rgba(226,232,240,0.95)',
                      boxShadow: '0 10px 30px rgba(15,23,42,0.05)',
                      transition:
                        'transform 220ms ease, box-shadow 220ms ease, border-color 220ms ease',
                      animation: `fadeUp 420ms ease ${index * 70}ms both`,
                      opacity: app.coming_soon ? 0.82 : 1,
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-6px)';
                      e.currentTarget.style.boxShadow =
                        '0 18px 40px rgba(15,23,42,0.10)';
                      e.currentTarget.style.borderColor = 'rgba(148,163,184,0.45)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow =
                        '0 10px 30px rgba(15,23,42,0.05)';
                      e.currentTarget.style.borderColor = 'rgba(226,232,240,0.95)';
                    }}
                  >
                    <Box
                      style={{
                        position: 'absolute',
                        top: -30,
                        right: -30,
                        width: 100,
                        height: 100,
                        borderRadius: '50%',
                        background: `var(--mantine-color-${app.color}-0)`,
                        opacity: 0.9,
                      }}
                    />

                    <Stack gap="md" style={{ position: 'relative', zIndex: 2 }}>
                      <Group justify="space-between" align="flex-start">
                        <ThemeIcon
                          size={58}
                          radius="xl"
                          variant="light"
                          color={app.color}
                          style={{
                            boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.3)',
                          }}
                        >
                          <IconComponent
                            style={{ width: rem(28), height: rem(28) }}
                            stroke={1.7}
                          />
                        </ThemeIcon>

                        {app.coming_soon ? (
                          <Badge radius="xl" color="gray" variant="filled">
                            Pek Yakında
                          </Badge>
                        ) : statText ? (
                          <Badge radius="xl" color={app.color} variant="light">
                            {statText}
                          </Badge>
                        ) : (
                          <Badge radius="xl" color="green" variant="dot">
                            Aktif
                          </Badge>
                        )}
                      </Group>

                      <div>
                        <Title order={4} fw={800} mb={8}>
                          {app.title}
                        </Title>
                        <Text size="sm" c="dimmed" lh={1.7}>
                          {app.description}
                        </Text>
                      </div>
                    </Stack>

                    <Divider my="md" />

                    <Group justify="space-between" align="center">
                      <Text size="xs" fw={700} tt="uppercase" c="dimmed">
                        {app.coming_soon ? 'Hazırlanıyor' : 'Modüle git'}
                      </Text>

                      <ActionIcon
                        radius="xl"
                        size={36}
                        variant="light"
                        color={app.color}
                      >
                        {app.coming_soon ? (
                          <IconRocket size={18} />
                        ) : (
                          <IconArrowUpRight size={18} />
                        )}
                      </ActionIcon>
                    </Group>
                  </Paper>
                );
              })}
            </SimpleGrid>
          )}
        </Stack>
      </Stack>

      {/* YEMEKHANE MODALI */}
      <Modal
        opened={isMenuModalOpen}
        onClose={() => setIsMenuModalOpen(false)}
        title={<Text fw={800} size="lg">Aylık Yemek Menüsü</Text>}
        size="lg"
        centered
        radius="xl"
        overlayProps={{ blur: 3 }}
      >
        {menuData.loading ? (
          <Center h={300}>
            <Loader color="red" />
          </Center>
        ) : (
          <Stack gap="md">
            <Paper withBorder radius="xl" style={{ overflow: 'hidden' }}>
              <Image
                src={menuData.menu_url}
                fallbackSrc="https://placehold.co/800x1200?text=Menü+Görseli+Yükleniyor..."
                alt="Yemek Menüsü"
                fit="contain"
              />
            </Paper>

            <Group justify="space-between">
              <Text size="xs" c="dimmed">
                Son Güncelleme: {menuData.updated_at || 'Belirtilmedi'}
              </Text>
              <Button color="red" radius="xl" size="xs" onClick={() => setIsMenuModalOpen(false)}>
                Kapat
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>

      {/* TELEFON REHBERİ MODALI */}
      <PhoneDirectoryModal
        opened={isPhoneModalOpen}
        onClose={() => setIsPhoneModalOpen(false)}
      />

      {/* GELECEK ÖZELLİK MODALI */}
      <Modal
        opened={isComingSoonOpen}
        onClose={() => setIsComingSoonOpen(false)}
        title="Bilgi"
        centered
        radius="xl"
      >
        <Stack align="center" py="xl">
          <ThemeIcon size={72} radius="xl" color="blue" variant="light">
            <IconRocket size={36} />
          </ThemeIcon>
          <Text fw={800} size="lg">
            {activeModuleName} yakında!
          </Text>
          <Text size="sm" c="dimmed" ta="center">
            {activeModuleDescription}
          </Text>
          <Text size="sm" c="dimmed" ta="center">
            Bu modül şu anda geliştirme aşamasındadır.
          </Text>
          <Button radius="xl" variant="light" onClick={() => setIsComingSoonOpen(false)}>
            Anladım
          </Button>
        </Stack>
      </Modal>

      {/* SORU GÖRÜŞ ÖNERİ MODALI */}
      <Modal
        opened={isFeedbackModalOpen}
        onClose={() => {
          setIsFeedbackModalOpen(false);
          setFeedbackMessage('');
          setFeedbackSuccess(false);
        }}
        title="Soru / Görüş / Öneri"
        size="md"
        centered
        radius="xl"
        overlayProps={{ blur: 3 }}
      >
        {feedbackSuccess ? (
          <Stack align="center" py="xl">
            <ThemeIcon size={64} radius="xl" color="green" variant="light">
              <IconMessageCircle2 size={30} />
            </ThemeIcon>
            <Text fw={800} c="green">
              Mesajınız başarıyla gönderildi!
            </Text>
            <Button
              radius="xl"
              onClick={() => {
                setIsFeedbackModalOpen(false);
                setFeedbackMessage('');
                setFeedbackSuccess(false);
              }}
            >
              Kapat
            </Button>
          </Stack>
        ) : (
          <Stack>
            <Text size="sm" c="dimmed">
              Mesajınızı bırakın, ekibimiz en kısa sürede yanıtlayacaktır.
            </Text>

            <Textarea
              placeholder="Mesajınız..."
              value={feedbackMessage}
              onChange={(e) => setFeedbackMessage(e.currentTarget.value)}
              minRows={5}
              autosize
              radius="lg"
            />

            <Button
              color="pink"
              radius="xl"
              loading={feedbackSending}
              onClick={async () => {
                if (!feedbackMessage.trim()) return;

                setFeedbackSending(true);
                try {
                  await api.post('/dashboard/feedback', {
                    message: feedbackMessage.trim(),
                  });

                  setFeedbackSuccess(true);
                  setFeedbackMessage('');
                } catch (e) {
                  console.error(e);
                  alert('Mesaj gönderilemedi!');
                } finally {
                  setFeedbackSending(false);
                }
              }}
            >
              Gönder
            </Button>
          </Stack>
        )}
      </Modal>

      <style>
        {`
          @keyframes fadeUp {
            from {
              opacity: 0;
              transform: translateY(14px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
        `}
      </style>
    </Container>
  );
}